import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Space, Typography, message as antdMessage } from 'antd';
import api from '../../utils/api';

const { Title } = Typography;

const statusMap = {
  unread: { label: '未读', color: 'orange' },
  read: { label: '已读', color: 'green' },
};

const MessageList = () => {
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const loadMessages = useCallback(
    async (page = pagination.page, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await api.get('/app/messages', {
          params: {
            page,
            limit,
          },
        });
        const data = response.data || {};
        
        // 数据转换：兼容后端返回的不同格式
        const normalizedMessages = (data.list || []).map(msg => ({
          id: msg.id || msg.message_id,  // 兼容两种ID字段名
          title: msg.title || '无标题',
          content: msg.content || '无内容',
          messageType: msg.messageType || msg.message_type,  // 兼容驼峰和下划线
          status: msg.status,
          isRead: msg.isRead !== undefined ? msg.isRead : msg.status === 'read',  // 兼容两种方式
          createdAt: msg.createdAt || msg.created_at,  // 兼容两种日期字段名
          readAt: msg.readAt || msg.read_at
        }));
        
        setMessages(normalizedMessages);
        const pager = data.pagination || {};
        setPagination({
          page: pager.page || pager.current || page,
          limit: pager.limit || pager.pageSize || limit,
          total: pager.total || 0,
        });
      } catch (error) {
        antdMessage.error(error.response?.data?.message || '获取消息失败');
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.limit]
  );

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleTableChange = ({ current, pageSize }) => {
    loadMessages(current, pageSize);
  };

  const markAsRead = async (record) => {
    try {
      // 后端 app 接口使用 POST 方法
      await api.post(`/app/messages/${record.id}/read`);
      antdMessage.success('消息已标记为已读');
      loadMessages();
    } catch (error) {
      antdMessage.error(error.response?.data?.message || '操作失败');
    }
  };

  const markAllAsRead = async () => {
    try {
      // 后端 app 接口使用 POST 方法
      await api.post('/app/messages/read-all');
      antdMessage.success('全部消息已标记为已读');
      loadMessages();
    } catch (error) {
      antdMessage.error(error.response?.data?.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '类型',
      dataIndex: 'messageType',
      width: 100,
      render: (type) => {
        const typeMap = {
          'reward': { text: '奖励通知', color: 'green' },
          'logistics': { text: '物流通知', color: 'blue' },
          'fund': { text: '资金通知', color: 'orange' },
          'security': { text: '安全通知', color: 'red' },
          'operation': { text: '运营通知', color: 'purple' },
        };
        const config = typeMap[type] || { text: type || '未知', color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '标题',
      dataIndex: 'title',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <span>{text}</span>
          <Tag color={statusMap[record.status]?.color || 'default'}>
            {statusMap[record.status]?.label || record.status}
          </Tag>
        </Space>
      ),
    },
    {
      title: '内容',
      dataIndex: 'content',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '--'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          {record.status === 'unread' && (
            <Button type="link" onClick={() => markAsRead(record)}>
              标记已读
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Title level={3}>站内信</Title>
      <Card
        extra={
          <Space>
            <Button onClick={() => loadMessages()}>刷新</Button>
            <Button type="primary" onClick={markAllAsRead}>
              全部标记已读
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={messages}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </Space>
  );
};

export default MessageList;







